import React, { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { MapPin, Wallet, Users, ListChecks, Lightbulb, Globe, LogOut, User, Church } from "lucide-react";
import { CompassLogo } from "@/components/CompassLogo";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { UI, t } from "@/i18n/translations";
import { ProfileSheet } from "@/components/ProfileSheet";

const TABS = [
  { to: "/app/reperes", key: "reperes", Icon: MapPin },
  { to: "/app/budget", key: "budget", Icon: Wallet },
  { to: "/app/reseau", key: "reseau", Icon: Users },
  { to: "/app/demarches", key: "demarches", Icon: ListChecks },
  { to: "/app/astuces", key: "astuces", Icon: Lightbulb },
  { to: "/app/communaute", key: "communaute", Icon: Church },
];

export function Layout() {
  const { lang, toggle } = useLang();
  const { user, logout } = useAuth();
  const [profileOpen, setProfileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-paper text-ink flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-paper/80 border-b border-line">
        <div className="max-w-md mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <CompassLogo size={32} />
            <span className="font-display text-xl font-extrabold tracking-tight">Boussole</span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              data-testid="lang-toggle"
              onClick={toggle}
              className="flex items-center gap-1.5 rounded-full border border-line bg-white px-3 py-2 text-sm font-semibold hover:border-amber transition-colors"
            >
              <Globe className="w-4 h-4 text-amber" strokeWidth={2} />
              {lang.toUpperCase()}
            </button>
            <button
              data-testid="open-profile-btn"
              onClick={() => setProfileOpen(true)}
              className="grid place-items-center w-10 h-10 rounded-full border border-line bg-white hover:border-amber transition-colors"
              aria-label="profile"
            >
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <User className="w-5 h-5" strokeWidth={2} />
              )}
            </button>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="grid place-items-center w-10 h-10 rounded-full border border-line bg-white hover:border-coral hover:text-coral transition-colors"
              aria-label="logout"
            >
              <LogOut className="w-5 h-5" strokeWidth={2} />
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 w-full max-w-md mx-auto px-4 pt-5 pb-28">
        <Outlet />
      </main>

      {/* Bottom tabs */}
      <nav className="fixed bottom-0 inset-x-0 z-30 border-t border-line bg-paper/90 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]">
        <div className="max-w-md mx-auto grid grid-cols-6">
          {TABS.map(({ to, key, Icon }) => (
            <NavLink
              key={key}
              to={to}
              data-testid={`tab-${key}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-2.5 min-h-[56px] transition-colors ${
                  isActive ? "text-amber" : "text-ink/50 hover:text-ink"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className={`w-[22px] h-[22px] transition-transform ${isActive ? "scale-110" : ""}`}
                    strokeWidth={isActive ? 2.5 : 2}
                  />
                  <span className="text-[11px] font-semibold leading-none">{t(UI.nav[key], lang)}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      <ProfileSheet open={profileOpen} onClose={() => setProfileOpen(false)} />
    </div>
  );
}
